import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Typography,
} from "@mui/material";
import { useSelector } from "react-redux";
import { Navigate, useSearchParams } from "react-router-dom";
import NewMiniDrawer from "../../components/navMenu/menu-nav";
import freightApi2 from "../../service/modules/freight.api";
import userService from "../../service/user.service";
import FreightFilters from "./components/FreightFilters";
import FreightList from "./components/FreightList";
import FreightDetailPanel from "./components/FreightDetailPanel";
import CarrierHubSummary from "./components/CarrierHubSummary";
import { EMPTY_FILTERS, formatFreightValueDisplay } from "./mockData";
import { isPriceVisibilityMasked } from "../../util/freight/carrierPriceVisibility";
import { markLancesFreightsAsSeen, markLancesAcceptedAsSeen } from "../../util/freight/lancesNewFreightsBadge";
import {
  PAGE_SIZE,
  buildEnterQueuePayload,
  buildLeaveQueuePayload,
  buildSubmitBidPayload,
  getApiErrorMessage,
  getShipperDisplayName,
  isBidFreight,
  mapAvailableDetail,
  matchesCollectionDateFilter,
  unwrapAvailableDetail,
} from "./availableFreight.mapper";
import {
  EMPTY_HUB_SUMMARY,
  HUB_TAB_AVAILABLE,
  HUB_TAB_IN_REVIEW,
  HUB_TAB_ACCEPTED,
  HUB_TABS,
  fetchAllCarrierHubPages,
  filterFreightsByRoute,
  mapCarrierHubListItem,
  sortCarrierHubFreights,
} from "./carrierHub.mapper";
import "./lances.css";

export default function LancesPage() {
  const isLoggedIn = useSelector((state) => state.usuarioLogado) > 0;
  const user = useSelector((state) => state.user);
  const shipper = useSelector((state) => state.shipper);
  const [searchParams, setSearchParams] = useSearchParams();

  const viewerShipperIds = useMemo(
    () => userService.getShipperIdsFromUser(user),
    [user?.uidShipper, user?.uidShipperAffiliates],
  );

  const [filters, setFilters] = useState({ ...EMPTY_FILTERS });
  const [appliedFilters, setAppliedFilters] = useState({ ...EMPTY_FILTERS });
  const [activeTab, setActiveTab] = useState(HUB_TAB_AVAILABLE);
  const [summary, setSummary] = useState({ ...EMPTY_HUB_SUMMARY });
  const [allFreights, setAllFreights] = useState([]);
  const [listTotal, setListTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [listError, setListError] = useState("");
  const [page, setPage] = useState(1);
  const [selectedFreightId, setSelectedFreightId] = useState(null);
  const [selectedFreightDetail, setSelectedFreightDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [confirmAcceptFreight, setConfirmAcceptFreight] = useState(null);
  const [confirmBid, setConfirmBid] = useState(null);
  const [confirmReply, setConfirmReply] = useState(null);
  const [confirmAcceptCounter, setConfirmAcceptCounter] = useState(null);
  const [acceptingFreightId, setAcceptingFreightId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, severity: "info", message: "" });

  const detailRequestRef = useRef(0);
  const filterLoadingIntentRef = useRef(false);
  const hubRequestRef = useRef(0);

  const shipperDisplayName = useMemo(
    () => getShipperDisplayName(shipper, user?.name || ""),
    [shipper, user?.name],
  );

  const loadHub = useCallback(
    async (activeFilters, tab, { fromFilter = false } = {}) => {
      const requestId = ++hubRequestRef.current;

      if (fromFilter) {
        setFilterLoading(true);
      } else {
        setLoading(true);
      }
      setListError("");

      try {
        const idCarrier = user?.uidShipper || undefined;
        const idUser = user?.uid || undefined;
        if (!idCarrier) {
          if (requestId !== hubRequestRef.current) return;
          setSummary({ ...EMPTY_HUB_SUMMARY });
          setAllFreights([]);
          setListTotal(0);
          setListError(
            "Usuário sem transportadora vinculada. Não é possível carregar o hub.",
          );
          return;
        }

        const { summary: hubSummary, items, pagination } =
          await fetchAllCarrierHubPages(
            (params) => freightApi2.getCarrierHub(params),
            activeFilters,
            { tab, idCarrier, idUser },
          );

        if (requestId !== hubRequestRef.current) return;

        const hasRouteFilter = Boolean(
          activeFilters?.origin?.trim() || activeFilters?.destination?.trim(),
        );
        const hasClientSideFilter =
          hasRouteFilter || Boolean(activeFilters?.collectionDate);

        const mapped = sortCarrierHubFreights(
          filterFreightsByRoute(
            (items || [])
              .map((item) => mapCarrierHubListItem(item, { viewerShipperIds }))
              .filter((freight) => freight?.id)
              .filter((freight) =>
                matchesCollectionDateFilter(
                  freight.collectionDate,
                  activeFilters?.collectionDate,
                ),
              ),
            activeFilters,
          ),
          activeFilters?.sort,
        );

        setSummary(hubSummary || { ...EMPTY_HUB_SUMMARY });
        setAllFreights(mapped);
        setListTotal(
          hasClientSideFilter
            ? mapped.length
            : typeof pagination?.total === "number"
              ? pagination.total
              : mapped.length,
        );

        const viewerKey = String(user?.uid || user?.uidShipper || "").trim();
        if (viewerKey && tab === HUB_TAB_AVAILABLE) {
          markLancesFreightsAsSeen(
            viewerKey,
            mapped.map((freight) => freight.id),
          );
        }
        if (viewerKey && tab === HUB_TAB_ACCEPTED) {
          markLancesAcceptedAsSeen(
            viewerKey,
            mapped.map((freight) => freight.id),
          );
        }
      } catch (error) {
        if (requestId !== hubRequestRef.current) return;
        console.error("[LancesPage] Erro ao carregar carrier-hub:", error);
        setListError(
          getApiErrorMessage(
            error,
            "Não foi possível carregar os fretes disponíveis.",
          ),
        );
        setSummary({ ...EMPTY_HUB_SUMMARY });
        setAllFreights([]);
        setListTotal(0);
      } finally {
        if (requestId === hubRequestRef.current) {
          setLoading(false);
          setFilterLoading(false);
        }
      }
    },
    [user?.uid, user?.uidShipper, viewerShipperIds],
  );

  const paginatedFreights = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return allFreights.slice(start, start + PAGE_SIZE);
  }, [allFreights, page]);

  const mergedDetailFreight = useMemo(() => {
    if (!selectedFreightId) return null;

    const fromList = allFreights.find((f) => f.id === selectedFreightId);
    if (!selectedFreightDetail && !fromList) return null;

    return {
      ...(fromList ?? {}),
      ...(selectedFreightDetail ?? {}),
      isInCarrierQueue: Boolean(
        selectedFreightDetail?.isInCarrierQueue || fromList?.isInCarrierQueue,
      ),
      isMasked: Boolean(
        selectedFreightDetail?.isMasked ??
          selectedFreightDetail?.priceVisibility?.isMasked ??
          fromList?.isMasked ??
          fromList?.priceVisibility?.isMasked,
      ),
      priceVisibility:
        selectedFreightDetail?.priceVisibility ?? fromList?.priceVisibility,
      freightValue:
        selectedFreightDetail?.priceVisibility?.displayPrice ??
        selectedFreightDetail?.freightValue ??
        fromList?.priceVisibility?.displayPrice ??
        fromList?.freightValue,
      shipper:
        selectedFreightDetail?.priceVisibility?.displayShipperName ??
        selectedFreightDetail?.shipper ??
        fromList?.priceVisibility?.displayShipperName ??
        fromList?.shipper,
      carrierQueue:
        selectedFreightDetail?.carrierQueue ?? fromList?.carrierQueue ?? null,
      carrierQueueJoinedAt:
        selectedFreightDetail?.carrierQueueJoinedAt ??
        fromList?.carrierQueueJoinedAt ??
        selectedFreightDetail?.carrierQueue?.dataHora ??
        fromList?.carrierQueue?.dataHora ??
        null,
      isMyClient: Boolean(
        selectedFreightDetail?.isMyClient ?? fromList?.isMyClient,
      ),
      cargoType: selectedFreightDetail?.cargoType ?? fromList?.cargoType ?? null,
      paymentTermLabel:
        selectedFreightDetail?.paymentTermLabel ??
        fromList?.paymentTermLabel ??
        null,
      contractingType:
        selectedFreightDetail?.contractingType ?? fromList?.contractingType,
      type: selectedFreightDetail?.type ?? fromList?.type,
      contractingLabel:
        selectedFreightDetail?.contractingLabel ?? fromList?.contractingLabel,
      yourBidAmount:
        selectedFreightDetail?.yourBidAmount ?? fromList?.yourBidAmount ?? null,
      canSubmitBid:
        selectedFreightDetail?.canSubmitBid ?? fromList?.canSubmitBid,
      counterAmount:
        selectedFreightDetail?.counterAmount ?? fromList?.counterAmount ?? null,
      replyAmount:
        selectedFreightDetail?.replyAmount ?? fromList?.replyAmount ?? null,
      negotiationTurn:
        selectedFreightDetail?.negotiationTurn ??
        fromList?.negotiationTurn ??
        null,
      negotiationStatusLabel:
        selectedFreightDetail?.negotiationStatusLabel ??
        fromList?.negotiationStatusLabel ??
        null,
      canAcceptCounter: Boolean(
        selectedFreightDetail?.canAcceptCounter ?? fromList?.canAcceptCounter,
      ),
      canReplyBid: Boolean(
        selectedFreightDetail?.canReplyBid ?? fromList?.canReplyBid,
      ),
      referenceValue:
        selectedFreightDetail?.referenceValue ??
        fromList?.referenceValue ??
        null,
      tollAmount:
        selectedFreightDetail?.tollAmount || fromList?.tollAmount || null,
      anttValue:
        selectedFreightDetail?.anttValue ?? fromList?.anttValue ?? null,
      bidSubmittedAt:
        selectedFreightDetail?.bidSubmittedAt ??
        fromList?.bidSubmittedAt ??
        null,
      bidPaymentTermDays:
        selectedFreightDetail?.bidPaymentTermDays ??
        fromList?.bidPaymentTermDays ??
        null,
      weight: selectedFreightDetail?.weight ?? fromList?.weight ?? null,
      distanceKm:
        selectedFreightDetail?.distanceKm ?? fromList?.distanceKm ?? null,
      merchandiseDescription:
        selectedFreightDetail?.merchandiseDescription ??
        fromList?.merchandiseDescription ??
        null,
      observation:
        selectedFreightDetail?.observation ?? fromList?.observation ?? null,
      observations:
        selectedFreightDetail?.observations ??
        fromList?.observations ??
        [],
    };
  }, [selectedFreightId, selectedFreightDetail, allFreights]);

  const syncFreightQueueFromDetail = useCallback((freightId, mapped) => {
    if (!freightId || !mapped) return;

    setAllFreights((prev) =>
      prev.map((f) =>
        f.id === freightId
          ? {
              ...f,
              isInCarrierQueue: mapped.isInCarrierQueue,
              carrierQueue: mapped.carrierQueue ?? f.carrierQueue,
              carrierQueueJoinedAt:
                mapped.carrierQueueJoinedAt ??
                mapped.carrierQueue?.dataHora ??
                f.carrierQueueJoinedAt,
            }
          : f,
      ),
    );
  }, []);

  const fetchFreightDetail = useCallback(
    async (freightId) => {
      if (!freightId) {
        setSelectedFreightDetail(null);
        setDetailError("");
        return;
      }

      const requestId = detailRequestRef.current + 1;
      detailRequestRef.current = requestId;
      setDetailLoading(true);
      setDetailError("");
      setSelectedFreightDetail(null);

      try {
        const idCarrier = user?.uidShipper || undefined;
        const idUser = user?.uid || undefined;
        let mapped = null;

        try {
          const hubDetail = await freightApi2.getCarrierHubById(
            freightId,
            idCarrier,
            idUser,
          );
          const rawHub =
            hubDetail?.data?.data ?? hubDetail?.data ?? hubDetail;
          if (rawHub && (rawHub.freightId || rawHub.id)) {
            mapped = mapCarrierHubListItem(
              {
                ...rawHub,
                id: rawHub.freightId || rawHub.id,
              },
              { viewerShipperIds },
            );
            // Preferir mapAvailableDetail para campos ricos do detalhe se o payload for completo
            const asDetail = mapAvailableDetail(rawHub, { viewerShipperIds });
            mapped = { ...asDetail, ...mapped, id: asDetail.id || mapped.id };
          }
        } catch (hubDetailError) {
          // Fallback para available by id enquanto o detail do hub estabiliza
          console.warn(
            "[LancesPage] carrier-hub detail indisponível, usando available:",
            hubDetailError,
          );
        }

        if (!mapped) {
          const response = await freightApi2.getAvailableFreightById(
            freightId,
            idCarrier,
            idUser,
          );
          const raw = unwrapAvailableDetail(response);
          mapped = mapAvailableDetail(raw, { viewerShipperIds });
        }

        if (requestId !== detailRequestRef.current) return;

        setSelectedFreightDetail(mapped);
        syncFreightQueueFromDetail(freightId, mapped);
      } catch (error) {
        if (requestId !== detailRequestRef.current) return;
        console.error("[LancesPage] Erro ao carregar detalhe:", error);
        setDetailError(
          getApiErrorMessage(
            error,
            "Não foi possível carregar os detalhes do frete.",
          ),
        );
      } finally {
        if (requestId === detailRequestRef.current) {
          setDetailLoading(false);
        }
      }
    },
    [user?.uid, user?.uidShipper, viewerShipperIds, syncFreightQueueFromDetail],
  );

  useEffect(() => {
    const tabFromUrl = searchParams.get("tab");
    const isValidTab = HUB_TABS.some((tab) => tab.key === tabFromUrl);
    if (isValidTab && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams, activeTab]);

  useEffect(() => {
    if (!isLoggedIn) return;
    setPage(1);
    const fromFilter = filterLoadingIntentRef.current;
    filterLoadingIntentRef.current = false;
    loadHub(appliedFilters, activeTab, { fromFilter });
  }, [isLoggedIn, appliedFilters, activeTab, loadHub]);

  useEffect(() => {
    fetchFreightDetail(selectedFreightId);
  }, [selectedFreightId, fetchFreightDetail]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(allFreights.length / PAGE_SIZE));
    if (page > totalPages) setPage(totalPages);
  }, [allFreights.length, page]);

  const handleSearch = () => {
    filterLoadingIntentRef.current = true;
    setAppliedFilters({ ...filters });
    setPage(1);
    setSelectedFreightId(null);
    setSelectedFreightDetail(null);
  };

  const handleClear = () => {
    filterLoadingIntentRef.current = true;
    setFilters({ ...EMPTY_FILTERS });
    setAppliedFilters({ ...EMPTY_FILTERS });
    setPage(1);
    setSelectedFreightId(null);
    setSelectedFreightDetail(null);
  };

  const handleTabChange = (nextTab) => {
    if (!nextTab || nextTab === activeTab) return;
    filterLoadingIntentRef.current = true;
    setActiveTab(nextTab);
    setPage(1);
    setSelectedFreightId(null);
    setSelectedFreightDetail(null);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (nextTab === HUB_TAB_AVAILABLE) next.delete("tab");
        else next.set("tab", nextTab);
        return next;
      },
      { replace: true },
    );
  };

  const handlePageChange = (nextPage) => {
    const totalPages = Math.max(1, Math.ceil(allFreights.length / PAGE_SIZE));
    if (nextPage < 1 || nextPage > totalPages || nextPage === page) return;
    setPage(nextPage);
  };

  const handleSelectFreight = (id) => {
    setSelectedFreightId(id);
  };

  const handleCloseDetail = () => {
    setSelectedFreightId(null);
    setSelectedFreightDetail(null);
    setDetailError("");
  };

  const markFreightQueueStatus = useCallback(
    (freightId, isInCarrierQueue) => {
      setAllFreights((prev) =>
        prev.map((f) =>
          f.id === freightId
            ? {
                ...f,
                isInCarrierQueue,
                carrierQueue: isInCarrierQueue ? f.carrierQueue : null,
                carrierQueueJoinedAt: isInCarrierQueue
                  ? f.carrierQueueJoinedAt
                  : null,
              }
            : f,
        ),
      );

      if (selectedFreightId === freightId) {
        setSelectedFreightDetail((prev) =>
          prev
            ? {
                ...prev,
                isInCarrierQueue,
                carrierQueue: isInCarrierQueue ? prev.carrierQueue : null,
                carrierQueueJoinedAt: isInCarrierQueue
                  ? prev.carrierQueueJoinedAt
                  : null,
              }
            : prev,
        );
      }
    },
    [selectedFreightId],
  );

  const refreshHubAfterQueueChange = useCallback(async () => {
    await loadHub(appliedFilters, activeTab, { fromFilter: false });
  }, [loadHub, appliedFilters, activeTab]);

  const handleLeaveQueue = async (freight) => {
    if (!freight?.id || acceptingFreightId) return;

    const freightId = freight.id;
    setAcceptingFreightId(freightId);

    try {
      const payload = buildLeaveQueuePayload(user);
      const response = await freightApi2.leaveTransportQueue(freightId, payload);
      const successMessage =
        response?.data?.message || "Você saiu da fila deste frete.";

      markFreightQueueStatus(freightId, false);
      setSnackbar({
        open: true,
        severity: "success",
        message: successMessage,
      });
      await refreshHubAfterQueueChange();
    } catch (error) {
      console.error("[LancesPage] Erro ao sair da fila:", error);
      setSnackbar({
        open: true,
        severity: "error",
        message: getApiErrorMessage(
          error,
          "Não foi possível sair da fila do frete.",
        ),
      });
    } finally {
      setAcceptingFreightId(null);
    }
  };

  const handleAcceptFreight = (freight) => {
    if (!freight) return;

    if (isBidFreight(freight)) {
      setSelectedFreightId(freight.id);
      return;
    }

    // Enter/leave fila só faz sentido em Disponíveis / Em análise
    const canManageQueue =
      activeTab === HUB_TAB_AVAILABLE || activeTab === HUB_TAB_IN_REVIEW;

    if (!canManageQueue) {
      setSelectedFreightId(freight.id);
      return;
    }

    if (isPriceVisibilityMasked(freight) && !freight.isInCarrierQueue) {
      setSnackbar({
        open: true,
        severity: "info",
        message:
          "Este frete está disponível apenas para visualização (Valor Anunciado).",
      });
      return;
    }

    if (freight.isInCarrierQueue) {
      handleLeaveQueue(freight);
      return;
    }

    if (!user?.uidShipper) {
      setSnackbar({
        open: true,
        severity: "error",
        message:
          "Usuário sem transportadora vinculada. Não é possível entrar na fila.",
      });
      return;
    }

    setConfirmAcceptFreight(freight);
  };

  const handleCancelAccept = () => {
    if (acceptingFreightId) return;
    setConfirmAcceptFreight(null);
  };

  const handleConfirmAccept = async () => {
    if (!confirmAcceptFreight || acceptingFreightId) return;

    const freightId = confirmAcceptFreight.id;
    setAcceptingFreightId(freightId);

    try {
      const payload = buildEnterQueuePayload(user, shipper);
      const response = await freightApi2.enterTransportQueue(freightId, payload);

      const successMessage =
        response?.data?.message ||
        "Você entrou na fila e receberá uma notificação quando o frete for contratado.";

      const joinedAt = new Date().toISOString();

      setAllFreights((prev) =>
        prev.map((f) =>
          f.id === freightId
            ? {
                ...f,
                isInCarrierQueue: true,
                carrierQueueJoinedAt: joinedAt,
                carrierQueue: {
                  inQueue: true,
                  idCarrier: user?.uidShipper || "",
                  nameCarrier: shipperDisplayName,
                  dataHora: joinedAt,
                },
              }
            : f,
        ),
      );

      if (selectedFreightId === freightId) {
        setSelectedFreightDetail((prev) =>
          prev
            ? {
                ...prev,
                isInCarrierQueue: true,
                carrierQueueJoinedAt: joinedAt,
                carrierQueue: {
                  inQueue: true,
                  idCarrier: user?.uidShipper || "",
                  nameCarrier: shipperDisplayName,
                  dataHora: joinedAt,
                },
              }
            : prev,
        );
      }

      setConfirmAcceptFreight(null);
      setSnackbar({
        open: true,
        severity: "success",
        message: successMessage,
      });
      await refreshHubAfterQueueChange();
    } catch (error) {
      console.error("[LancesPage] Erro ao aceitar frete:", error);
      setSnackbar({
        open: true,
        severity: "error",
        message: getApiErrorMessage(
          error,
          "Não foi possível entrar na fila do frete.",
        ),
      });
    } finally {
      setAcceptingFreightId(null);
    }
  };

  const handleSubmitBid = (freight, amount) => {
    if (!freight?.id) return;

    const parsedAmount = Number(amount);
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      setSnackbar({
        open: true,
        severity: "warning",
        message: "Informe o valor do lance.",
      });
      return;
    }

    if (!user?.uidShipper) {
      setSnackbar({
        open: true,
        severity: "error",
        message:
          "Usuário sem transportadora vinculada. Não é possível enviar lance.",
      });
      return;
    }

    setSelectedFreightId(freight.id);
    setConfirmBid({
      freight,
      amount: parsedAmount,
    });
  };

  const handleCancelBid = () => {
    if (acceptingFreightId) return;
    setConfirmBid(null);
  };

  const handleConfirmBid = async () => {
    if (!confirmBid?.freight?.id || acceptingFreightId) return;

    const freightId = confirmBid.freight.id;
    setAcceptingFreightId(freightId);

    try {
      const payload = buildSubmitBidPayload(
        user,
        shipper,
        confirmBid.amount,
      );
      const response = await freightApi2.submitBid(freightId, payload);
      const successMessage =
        response?.data?.message || "Lance enviado para análise do embarcador.";

      setAllFreights((prev) =>
        prev.map((f) =>
          f.id === freightId
            ? {
                ...f,
                yourBidAmount: confirmBid.amount,
                canSubmitBid: false,
                isInCarrierQueue: true,
                bidSubmittedAt: new Date().toISOString(),
                negotiationTurn: "shipper",
                negotiationStatusLabel:
                  "Lance enviado. Aguardando análise do embarcador.",
                canAcceptCounter: false,
                canReplyBid: false,
              }
            : f,
        ),
      );

      if (selectedFreightId === freightId) {
        setSelectedFreightDetail((prev) =>
          prev
            ? {
                ...prev,
                yourBidAmount: confirmBid.amount,
                canSubmitBid: false,
                isInCarrierQueue: true,
                bidSubmittedAt: new Date().toISOString(),
                negotiationTurn: "shipper",
                negotiationStatusLabel:
                  "Lance enviado. Aguardando análise do embarcador.",
                canAcceptCounter: false,
                canReplyBid: false,
              }
            : prev,
        );
      }

      setConfirmBid(null);
      setSnackbar({
        open: true,
        severity: "success",
        message: successMessage,
      });
      await refreshHubAfterQueueChange();
    } catch (error) {
      console.error("[LancesPage] Erro ao enviar lance:", error);
      setSnackbar({
        open: true,
        severity: "error",
        message: getApiErrorMessage(
          error,
          "Não foi possível enviar o lance deste frete.",
        ),
      });
    } finally {
      setAcceptingFreightId(null);
    }
  };

  const handleAcceptCounter = (freight) => {
    if (!freight?.id) return;
    if (!user?.uidShipper) {
      setSnackbar({
        open: true,
        severity: "error",
        message:
          "Usuário sem transportadora vinculada. Não é possível aceitar a contraproposta.",
      });
      return;
    }
    setSelectedFreightId(freight.id);
    setConfirmAcceptCounter(freight);
  };

  const handleCancelAcceptCounter = () => {
    if (acceptingFreightId) return;
    setConfirmAcceptCounter(null);
  };

  const handleConfirmAcceptCounter = async () => {
    if (!confirmAcceptCounter?.id || acceptingFreightId) return;
    const freightId = confirmAcceptCounter.id;
    setAcceptingFreightId(freightId);
    try {
      const payload = buildEnterQueuePayload(user, shipper);
      const response = await freightApi2.acceptFreightCounter(freightId, payload);
      setConfirmAcceptCounter(null);
      setSnackbar({
        open: true,
        severity: "success",
        message:
          response?.data?.message ||
          "Contraproposta aceita. O frete foi atribuído a você.",
      });
      await refreshHubAfterQueueChange();
    } catch (error) {
      console.error("[LancesPage] Erro ao aceitar contraproposta:", error);
      setSnackbar({
        open: true,
        severity: "error",
        message: getApiErrorMessage(
          error,
          "Não foi possível aceitar a contraproposta.",
        ),
      });
    } finally {
      setAcceptingFreightId(null);
    }
  };

  const handleSubmitReply = (freight, amount) => {
    if (!freight?.id) return;
    const parsedAmount = Number(amount);
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      setSnackbar({
        open: true,
        severity: "warning",
        message: "Informe o valor da tréplica.",
      });
      return;
    }
    if (!user?.uidShipper) {
      setSnackbar({
        open: true,
        severity: "error",
        message:
          "Usuário sem transportadora vinculada. Não é possível enviar tréplica.",
      });
      return;
    }
    setSelectedFreightId(freight.id);
    setConfirmReply({ freight, amount: parsedAmount });
  };

  const handleCancelReply = () => {
    if (acceptingFreightId) return;
    setConfirmReply(null);
  };

  const handleConfirmReply = async () => {
    if (!confirmReply?.freight?.id || acceptingFreightId) return;
    const freightId = confirmReply.freight.id;
    setAcceptingFreightId(freightId);
    try {
      const payload = buildSubmitBidPayload(
        user,
        shipper,
        confirmReply.amount,
      );
      const response = await freightApi2.replyFreightBid(freightId, payload);
      const nextState = {
        yourBidAmount: confirmReply.amount,
        replyAmount: confirmReply.amount,
        canSubmitBid: false,
        canAcceptCounter: false,
        canReplyBid: false,
        negotiationTurn: "shipper",
        negotiationStatusLabel:
          "Tréplica enviada. O embarcador só pode aceitar ou recusar.",
        isInCarrierQueue: true,
      };
      setAllFreights((prev) =>
        prev.map((f) => (f.id === freightId ? { ...f, ...nextState } : f)),
      );
      if (selectedFreightId === freightId) {
        setSelectedFreightDetail((prev) =>
          prev ? { ...prev, ...nextState } : prev,
        );
      }
      setConfirmReply(null);
      setSnackbar({
        open: true,
        severity: "success",
        message:
          response?.data?.message ||
          "Tréplica enviada. O embarcador só pode aceitar ou recusar.",
      });
      await refreshHubAfterQueueChange();
    } catch (error) {
      console.error("[LancesPage] Erro ao enviar tréplica:", error);
      setSnackbar({
        open: true,
        severity: "error",
        message: getApiErrorMessage(
          error,
          "Não foi possível enviar a tréplica deste frete.",
        ),
      });
    } finally {
      setAcceptingFreightId(null);
    }
  };

  if (!isLoggedIn) {
    return <Navigate to="/login" />;
  }

  const acceptValue = confirmAcceptFreight
    ? formatFreightValueDisplay(
        confirmAcceptFreight.freightValue,
        confirmAcceptFreight.pricingType,
      )
    : "";

  return (
    <NewMiniDrawer
      divOpen={
        <Box className="lances-page">
          <Box className="lances-page__header">
            <Typography variant="h5" className="lances-page__title">
              Central de BID
            </Typography>
            <Typography variant="body2" className="lances-page__subtitle">
              Encontre cargas para aceitar a preço fixo ou enviar seu lance.
            </Typography>
          </Box>

          <CarrierHubSummary
            summary={summary}
            activeTab={activeTab}
            onSelectTab={handleTabChange}
            loading={loading || filterLoading}
          />

          <Box className="lances-page__layout">
            <Box className="lances-page__main">
              <FreightFilters
                filters={filters}
                onChange={setFilters}
                onSearch={handleSearch}
                onClear={handleClear}
                loading={loading || filterLoading}
              />
              <FreightList
                freights={paginatedFreights}
                page={page}
                totalCount={Math.max(listTotal, allFreights.length)}
                loading={loading && !filterLoading}
                error={listError}
                selectedFreightId={selectedFreightId}
                acceptingFreightId={acceptingFreightId}
                activeTab={activeTab}
                summary={summary}
                onTabChange={handleTabChange}
                onSelectFreight={handleSelectFreight}
                onAcceptFreight={handleAcceptFreight}
                onPageChange={handlePageChange}
              />
            </Box>

            <Box className="lances-page__sidebar">
              <FreightDetailPanel
                freight={mergedDetailFreight}
                loading={detailLoading && !mergedDetailFreight}
                error={detailError}
                acceptingFreightId={acceptingFreightId}
                activeTab={activeTab}
                onClose={handleCloseDetail}
                onAcceptFreight={handleAcceptFreight}
                onSubmitBid={handleSubmitBid}
                onAcceptCounter={handleAcceptCounter}
                onSubmitReply={handleSubmitReply}
              />
            </Box>
          </Box>

          <Backdrop
            open={filterLoading}
            sx={{
              color: "#fff",
              zIndex: (theme) => theme.zIndex.modal + 1,
              backgroundColor: "rgba(1, 36, 67, 0.35)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <CircularProgress color="inherit" size={42} />
              <Typography variant="body2" sx={{ color: "#fff", fontWeight: 500 }}>
                Atualizando fretes...
              </Typography>
            </Box>
          </Backdrop>

          <Dialog
            open={Boolean(confirmAcceptFreight)}
            onClose={handleCancelAccept}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Confirmar entrada na fila do frete</DialogTitle>
            <DialogContent>
              <DialogContentText component="div">
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  Você está prestes a entrar na fila do frete{" "}
                  <strong>{confirmAcceptFreight?.number}</strong> por{" "}
                  <strong>{acceptValue}</strong>.
                </Typography>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  O frete ficará vinculado ao embarcador{" "}
                  <strong>{shipperDisplayName}</strong> caso seja escolhido.
                </Typography>
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button
                onClick={handleCancelAccept}
                disabled={Boolean(acceptingFreightId)}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                className="lances-btn-search"
                onClick={handleConfirmAccept}
                disabled={Boolean(acceptingFreightId)}
              >
                {acceptingFreightId ? (
                  <CircularProgress size={20} sx={{ color: "#fff" }} />
                ) : (
                  "Confirmar entrada na fila"
                )}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={Boolean(confirmBid)}
            onClose={handleCancelBid}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Confirmar lance</DialogTitle>
            <DialogContent>
              <DialogContentText component="div">
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  Enviar lance de{" "}
                  <strong>
                    {confirmBid
                      ? formatFreightValueDisplay(
                          confirmBid.amount,
                          confirmBid.freight?.pricingType,
                        )
                      : ""}
                  </strong>{" "}
                  no frete <strong>{confirmBid?.freight?.number}</strong>?
                </Typography>
                <Typography variant="body2">
                  O embarcador poderá aceitar, recusar ou enviar uma
                  contraproposta.
                </Typography>
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button
                onClick={handleCancelBid}
                disabled={Boolean(acceptingFreightId)}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                className="lances-btn-accept lances-btn-accept--open"
                onClick={handleConfirmBid}
                disabled={Boolean(acceptingFreightId)}
              >
                {acceptingFreightId ? (
                  <CircularProgress size={20} sx={{ color: "#fff" }} />
                ) : (
                  "Enviar lance"
                )}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={Boolean(confirmAcceptCounter)}
            onClose={handleCancelAcceptCounter}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Aceitar contraproposta</DialogTitle>
            <DialogContent>
              <DialogContentText component="div">
                <Typography variant="body2">
                  Aceitar a contraproposta de{" "}
                  <strong>
                    {formatFreightValueDisplay(
                      confirmAcceptCounter?.counterAmount,
                      confirmAcceptCounter?.pricingType,
                    )}
                  </strong>{" "}
                  no frete <strong>{confirmAcceptCounter?.number}</strong>? O
                  frete será atribuído a você.
                </Typography>
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button
                onClick={handleCancelAcceptCounter}
                disabled={Boolean(acceptingFreightId)}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                className="lances-btn-search"
                onClick={handleConfirmAcceptCounter}
                disabled={Boolean(acceptingFreightId)}
              >
                {acceptingFreightId ? (
                  <CircularProgress size={20} sx={{ color: "#fff" }} />
                ) : (
                  "Aceitar contraproposta"
                )}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={Boolean(confirmReply)}
            onClose={handleCancelReply}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Confirmar tréplica</DialogTitle>
            <DialogContent>
              <DialogContentText component="div">
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  Enviar tréplica de{" "}
                  <strong>
                    {confirmReply
                      ? formatFreightValueDisplay(
                          confirmReply.amount,
                          confirmReply.freight?.pricingType,
                        )
                      : ""}
                  </strong>{" "}
                  no frete <strong>{confirmReply?.freight?.number}</strong>?
                </Typography>
                <Typography variant="body2">
                  Depois da tréplica o embarcador só poderá aceitar ou recusar.
                </Typography>
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button
                onClick={handleCancelReply}
                disabled={Boolean(acceptingFreightId)}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                className="lances-btn-accept lances-btn-accept--open"
                onClick={handleConfirmReply}
                disabled={Boolean(acceptingFreightId)}
              >
                {acceptingFreightId ? (
                  <CircularProgress size={20} sx={{ color: "#fff" }} />
                ) : (
                  "Enviar tréplica"
                )}
              </Button>
            </DialogActions>
          </Dialog>

          <Snackbar
            open={snackbar.open}
            autoHideDuration={4000}
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              severity={snackbar.severity}
              onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
              variant="filled"
              sx={{ width: "100%" }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      }
    />
  );
}
